/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useState } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';

const GRAPHIC_COUNT = 3;
const GRAPHIC_INTERVAL_MS = 1200;
const GRAPHIC_BODY_HEIGHT = '152px';
const LINE_HEIGHT = '12px';
const CHIP_SIZE = '24px';

const styles = cssMap({
	wrapper: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		minHeight: GRAPHIC_BODY_HEIGHT,
		paddingBlock: token('space.300'),
		paddingInline: token('space.200'),
	},
	panel: {
		display: 'grid',
		alignContent: 'center',
		gap: token('space.150'),
		height: GRAPHIC_BODY_HEIGHT,
		width: '100%',
		maxWidth: '420px',
	},
	row: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
	},
	line: {
		height: LINE_HEIGHT,
		borderRadius: token('radius.full'),
		backgroundColor: token('color.skeleton'),
	},
	lineLong: {
		width: '86%',
	},
	lineMedium: {
		width: '68%',
	},
	lineShort: {
		width: '42%',
	},
	chip: {
		width: CHIP_SIZE,
		height: CHIP_SIZE,
		borderRadius: token('radius.full'),
		backgroundColor: token('color.background.accent.purple.subtlest'),
		flexShrink: 0,
	},
	chart: {
		display: 'flex',
		alignItems: 'end',
		gap: token('space.100'),
		height: '88px',
	},
	bar: {
		width: '20%',
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.accent.blue.subtler'),
	},
	barShort: {
		height: '34px',
	},
	barMedium: {
		height: '58px',
	},
	barTall: {
		height: '82px',
	},
	cardGrid: {
		display: 'grid',
		gridTemplateColumns: '1fr 1fr',
		gap: token('space.100'),
	},
	card: {
		height: '52px',
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.neutral.subtle.hovered'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
	},
});

const DocumentGraphic = (): JSX.Element => (
	<div css={styles.panel}>
		<div css={[styles.line, styles.lineLong]} />
		<div css={[styles.line, styles.lineMedium]} />
		<div css={[styles.line, styles.lineLong]} />
		<div css={[styles.line, styles.lineShort]} />
	</div>
);

const ListGraphic = (): JSX.Element => (
	<div css={styles.panel}>
		<div css={styles.row}>
			<div css={styles.chip} />
			<div css={[styles.line, styles.lineLong]} />
		</div>
		<div css={styles.row}>
			<div css={styles.chip} />
			<div css={[styles.line, styles.lineMedium]} />
		</div>
		<div css={styles.row}>
			<div css={styles.chip} />
			<div css={[styles.line, styles.lineShort]} />
		</div>
	</div>
);

const ChartGraphic = (): JSX.Element => (
	<div css={styles.panel}>
		<div css={styles.chart}>
			<div css={[styles.bar, styles.barMedium]} />
			<div css={[styles.bar, styles.barTall]} />
			<div css={[styles.bar, styles.barShort]} />
			<div css={[styles.bar, styles.barMedium]} />
		</div>
		<div css={styles.cardGrid}>
			<div css={styles.card} />
			<div css={styles.card} />
		</div>
	</div>
);

export const NoBodyExecutionLoading = ({ label }: { label: string }): JSX.Element => {
	const [graphicIndex, setGraphicIndex] = useState(0);

	useEffect(() => {
		const intervalId = setInterval(() => {
			setGraphicIndex((currentIndex) => (currentIndex + 1) % GRAPHIC_COUNT);
		}, GRAPHIC_INTERVAL_MS);

		return () => clearInterval(intervalId);
	}, []);

	const graphic =
		graphicIndex === 0 ? (
			<DocumentGraphic />
		) : graphicIndex === 1 ? (
			<ListGraphic />
		) : (
			<ChartGraphic />
		);

	return (
		<div css={styles.wrapper} data-testid="agent-managed-extension-no-body-loading" role="status">
			<VisuallyHidden>{label}</VisuallyHidden>
			{graphic}
		</div>
	);
};
